import { Volume2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/clientWeb/components/ui/select'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'
import { useDevicesStore } from '@/features/devices'

interface DeviceSelectorsProps {
  mics: MediaDeviceInfo[]
  speakers: MediaDeviceInfo[]
  cams: MediaDeviceInfo[]
  speakerTestRunning: boolean
  onTestSpeaker: () => void
}

export function DeviceSelectors({
  mics,
  speakers,
  cams,
  speakerTestRunning,
  onTestSpeaker,
}: DeviceSelectorsProps) {
  const t = useTranslation()
  const selectedMicId = useDevicesStore((s) => s.selectedMicId)
  const selectedCamId = useDevicesStore((s) => s.selectedCamId)
  const selectedSpeakerId = useDevicesStore((s) => s.selectedSpeakerId)
  const selectMic = useDevicesStore((s) => s.selectMic)
  const selectCam = useDevicesStore((s) => s.selectCam)
  const selectSpeaker = useDevicesStore((s) => s.selectSpeaker)

  // Before getUserMedia permission is granted, deviceId is "" — filter those out
  // so Radix never receives an empty-string value on SelectItem.
  const validMics = mics.filter((d) => !!d.deviceId)
  const validSpeakers = speakers.filter((d) => !!d.deviceId)
  const validCams = cams.filter((d) => !!d.deviceId)

  // Radix Select also rejects "" as the controlled value — coerce to undefined
  // so it shows the placeholder instead of throwing.
  const micValue = selectedMicId || undefined
  const speakerValue = selectedSpeakerId || undefined
  const camValue = selectedCamId || undefined

  return (
    <div className="mt-3 sm:mt-4 md:mt-5 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-2 sm:gap-3 px-1 sm:px-2">
      <div className="w-full sm:w-[200px] md:w-[220px]">
        <Select value={micValue} onValueChange={selectMic}>
          <SelectTrigger variant="outlined" className="w-full h-9 sm:h-10 text-sm dark:text-foreground">
            <SelectValue placeholder={t('dashboard.microphone')} />
          </SelectTrigger>
          <SelectContent>
            {validMics.length === 0 ? (
              <SelectItem value="no-mics" disabled>
                {t('dashboard.noMicrophonesFound')}
              </SelectItem>
            ) : (
              validMics.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {d.label || t('dashboard.microphone')}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
      <div className="w-full sm:w-[200px] md:w-[220px]">
        <Select value={speakerValue} onValueChange={selectSpeaker}>
          <SelectTrigger variant="outlined" className="w-full h-9 sm:h-10 text-sm dark:text-foreground">
            {speakerTestRunning ? (
              t('dashboard.playing')
            ) : (
              <SelectValue placeholder={t('dashboard.speaker')} />
            )}
          </SelectTrigger>
          <SelectContent>
            {validSpeakers.length === 0 ? (
              <SelectItem value="no-speakers" disabled>
                {t('dashboard.defaultOutput')}
              </SelectItem>
            ) : (
              validSpeakers.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {d.label || t('dashboard.speaker')}
                </SelectItem>
              ))
            )}
            <SelectSeparator />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onTestSpeaker()
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-foreground transition hover:bg-accent hover:text-accent-foreground"
            >
              <Volume2 className="h-4 w-4 opacity-80" />
              <span className="flex-1 text-left">{t('dashboard.testSpeakers')}</span>
            </button>
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 w-full sm:col-span-1 sm:w-[200px] md:w-[220px]">
        <Select value={camValue} onValueChange={selectCam}>
          <SelectTrigger variant="outlined" className="w-full h-9 sm:h-10 text-sm dark:text-foreground">
            <SelectValue placeholder={t('dashboard.camera')} />
          </SelectTrigger>
          <SelectContent>
            {validCams.length === 0 ? (
              <SelectItem value="no-cams" disabled>
                {t('dashboard.noCamerasFound')}
              </SelectItem>
            ) : (
              validCams.map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {d.label || t('dashboard.camera')}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
